const OPENAI_HARNESS_PREFIXES = [
  "opencloud-codex-direct-",
  "opencloud-codex-ai-sdk-harness-",
  "opencloud-opencode2-harness-",
] as const;
const ANTHROPIC_HARNESS_PREFIXES = [
  "opencloud-claude-code-harness-",
  "opencloud-opencode2-anthropic-harness-",
] as const;

export function requiresOpenAiProvider(harnessVersion: string): boolean {
  return OPENAI_HARNESS_PREFIXES.some((prefix) =>
    harnessVersion.startsWith(prefix),
  );
}

/** The model provider an Agent harness engine requires, if any. */
export function harnessProvider(
  harnessVersion: string,
): "openai" | "anthropic" | null {
  if (requiresOpenAiProvider(harnessVersion)) return "openai";
  return ANTHROPIC_HARNESS_PREFIXES.some((prefix) =>
    harnessVersion.startsWith(prefix),
  )
    ? "anthropic"
    : null;
}

/** Engines on OpenRouter models (decision 0031): Codex v3 with an OpenRouter provider block, and OpenCode 2. */
const OPENROUTER_HARNESS_PREFIXES = [
  "opencloud-codex-openrouter-harness-",
  "opencloud-opencode2-openrouter-harness-",
] as const;

/** The provider whose models an engine runs, OpenRouter included. */
function engineProvider(harnessVersion: string): "openai" | "anthropic" | "openrouter" | null {
  return OPENROUTER_HARNESS_PREFIXES.some((prefix) => harnessVersion.startsWith(prefix))
    ? "openrouter"
    : harnessProvider(harnessVersion);
}

const APP_OWNER_HARNESSES = new Set([
  "opencloud-codex-ai-sdk-harness-v3",
  "opencloud-claude-code-harness-v1",
  "opencloud-opencode2-harness-v1",
  "opencloud-opencode2-anthropic-harness-v1",
  "opencloud-opencode2-openrouter-harness-v1",
  "opencloud-codex-openrouter-harness-v1",
]);

/** App-owner harnesses share the same chat surface, including attachments. */
export function usesAppOwnerHarness(harnessVersion: string): boolean {
  return APP_OWNER_HARNESSES.has(harnessVersion);
}

/**
 * Whether the owner picks this conversation's provider, model and reasoning
 * in the composer: every engine that runs one provider's models does. A pick
 * of another provider moves the next root to that provider's engine.
 */
export function acceptsModelChoice(harnessVersion: string): boolean {
  return engineProvider(harnessVersion) !== null;
}

/**
 * The provider a new Agent connection must use under the default harness.
 * App-owner harnesses select their engine from the connection's provider.
 */
export function agentConnectionProvider(
  harnessVersion: string,
): "openai" | "anthropic" | null {
  return APP_OWNER_HARNESSES.has(harnessVersion)
    ? null
    : harnessProvider(harnessVersion);
}

/** Engine names shown per provider when the default harness picks by provider. */
export function agentEngineLabels(
  harnessVersion: string,
): Partial<Record<string, string>> | undefined {
  if (OPENCODE_ENGINES.has(harnessVersion)) {
    return { openai: "OpenCode", anthropic: "OpenCode" };
  }
  return APP_OWNER_HARNESSES.has(harnessVersion)
    ? { openai: "Codex", anthropic: "Claude Code" }
    : undefined;
}

const ENGINE_NAMES: Record<string, string> = {
  "opencloud-codex-ai-sdk-harness-v3": "Codex",
  "opencloud-claude-code-harness-v1": "Claude Code",
  "opencloud-opencode2-harness-v1": "OpenCode",
  "opencloud-opencode2-anthropic-harness-v1": "OpenCode",
  "opencloud-opencode2-openrouter-harness-v1": "OpenCode",
  "opencloud-codex-openrouter-harness-v1": "Codex",
};
/** Full harness names, with version, shown inside the model picker. */
const ENGINE_FULL_NAMES: Record<string, string> = {
  "opencloud-codex-ai-sdk-harness-v3": "Codex app-owner harness v3",
  "opencloud-claude-code-harness-v1": "Claude Code app-owner harness v1",
  "opencloud-opencode2-harness-v1": "OpenCode 2 harness v1 on OpenAI (experimental)",
  "opencloud-opencode2-anthropic-harness-v1": "OpenCode 2 harness v1 on Anthropic (experimental)",
  "opencloud-opencode2-openrouter-harness-v1": "OpenCode 2 harness v1 on OpenRouter (experimental)",
  "opencloud-codex-openrouter-harness-v1": "Codex OpenRouter harness v1",
};
const OPENCODE_ENGINES = new Set([
  "opencloud-opencode2-harness-v1",
  "opencloud-opencode2-anthropic-harness-v1",
  "opencloud-opencode2-openrouter-harness-v1",
]);

/** The provider of a qualified model id such as `openrouter/moonshotai/kimi-k3`. */
export function modelChoiceProvider(model: string | null | undefined): "openai" | "anthropic" | "openrouter" | null {
  const provider = model?.includes("/") ? model.slice(0, model.indexOf("/")) : null;
  return provider === "openai" || provider === "anthropic" || provider === "openrouter" ? provider : null;
}

/** An OpenCode harness on the given provider; other harnesses are returned unchanged. */
export function openCodeHarnessForProvider(
  harnessVersion: string,
  provider: "openai" | "anthropic" | "openrouter" | null | undefined,
): string {
  if (!OPENCODE_ENGINES.has(harnessVersion) || !provider) return harnessVersion;
  return defaultEngineFor(harnessVersion, provider);
}

/** The engine the installation default runs for a provider, as the service pairs them. */
function defaultEngineFor(defaultHarnessVersion: string, provider: "openai" | "anthropic" | "openrouter"): string {
  if (OPENCODE_ENGINES.has(defaultHarnessVersion)) {
    return provider === "anthropic" ? "opencloud-opencode2-anthropic-harness-v1"
      : provider === "openrouter" ? "opencloud-opencode2-openrouter-harness-v1" : "opencloud-opencode2-harness-v1";
  }
  if (APP_OWNER_HARNESSES.has(defaultHarnessVersion)) {
    return provider === "anthropic" ? "opencloud-claude-code-harness-v1"
      : provider === "openrouter" ? "opencloud-codex-openrouter-harness-v1" : "opencloud-codex-ai-sdk-harness-v3";
  }
  return defaultHarnessVersion;
}

/**
 * The engine name to show beside the model when a conversation does not run
 * the engine the installation default would pick for its provider; null when
 * it does, so the usual case stays uncluttered.
 */
export function nonDefaultEngineLabel(harnessVersion: string | null | undefined, defaultHarnessVersion: string): string | null {
  return nonDefaultEngine(harnessVersion, defaultHarnessVersion)?.label ?? null;
}

/** The short and full names of a non-default engine, or null for the default's own choice. */
export function nonDefaultEngine(
  harnessVersion: string | null | undefined,
  defaultHarnessVersion: string,
): { label: string; fullName: string } | null {
  if (!harnessVersion) return null;
  const provider = engineProvider(harnessVersion);
  if (!provider || harnessVersion === defaultEngineFor(defaultHarnessVersion, provider)) return null;
  const label = ENGINE_NAMES[harnessVersion];
  return label ? { label, fullName: ENGINE_FULL_NAMES[harnessVersion] ?? label } : null;
}
